<template>
  <div class="position-page-header">
    <!-- Breadcrumb Section -->
    <ExBreadcrumbs
      v-if="has_breadcrumb"
      class="position-page-header__breadcrumbs"
      :breadcrumbsList="breadcrumbsList"
      @onClickBreadcrumb="handleBreadcrumbClick"
    />

    <!-- Main Header Section -->
    <div class="position-page-header__main">
      <div class="position-page-header__left">
        <!-- Position Icon -->
        <svg
          width="60"
          height="60"
          viewBox="0 0 60 60"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M56.7863 52.5V15C56.7863 12.2386 54.5477 10 51.7863 10H8.21484C5.45342 10 3.21484 12.2386 3.21484 15V45"
            stroke="#666666"
            stroke-width="3.75"
            stroke-linecap="round"
          />
          <path
            d="M3.21484 22.5L56.7863 22.5"
            stroke="#666666"
            stroke-width="3.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M3.21484 23.3936V47.5012C3.21484 50.2626 5.45342 52.5012 8.21484 52.5012H22.4995C25.2609 52.5012 27.4995 50.2626 27.4995 47.5012V23.3936"
            stroke="#666666"
            stroke-width="3.75"
            stroke-linecap="round"
          />
          <path
            d="M12.3213 30H18.3925"
            stroke="#666666"
            stroke-width="3.25"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M12.3213 45H18.3925"
            stroke="#666666"
            stroke-width="3.25"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M5 37.5L27.5 37.5"
            stroke="#666666"
            stroke-width="3.25"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <!-- Position Info -->
        <div class="position-page-header__info">
          <!-- Status Badge and Name Row -->
          <div class="position-page-header__meta">
            <div class="position-page-header__status-row">
              <OrgObjectStatusBadge
                v-if="status"
                :status="status"
                class="position-page-header__status"
              />
              <ReadinessChip
                v-if="ideaMdp450Enabled && readiness"
                :readiness="readiness"
              />
              <template v-if="showExpandedDetails">
                <!-- Department control is hidden on RETIRED/ARCHIVED positions
                     (always orphans, never members — Positions Rule 2). -->
                <template v-if="canAssignDepartment">
                  <DepartmentTag
                    v-if="!ideaMdp440Enabled"
                    :state="has_breadcrumb ? 'Assign' : 'Assigned Removable'"
                    :label="departmentLabel"
                    @onClick="handleDepartmentAssign"
                    @onRemove="handleDepartmentRemove"
                  />
                  <template v-else>
                    <DepartmentTagInPositionForm
                      :department="resolvedDepartment"
                      @assign="openDepartmentDialog"
                      @remove="handleDepartmentRemove"
                    />
                    <DepartmentPickerDialog
                      :value="departmentDialogOpen"
                      :departmentTree="departmentTree"
                      :loading="isAssigningDepartment"
                      @input="departmentDialogOpen = $event"
                      @close="departmentDialogOpen = false"
                      @assign="handleDepartmentPickerAssign"
                    />
                    <DetachPositionDialog
                      v-if="showDetachDepartmentDialog"
                      v-model="showDetachDepartmentDialog"
                      parent-kind="department"
                      action-label="Remove"
                      :position_name="positionReleaseLabel"
                      :parent_name="departmentLeafName"
                      @onAction="handleConfirmReleaseDepartment"
                    />
                  </template>
                </template>
                <!-- Job Family control — same RETIRED/ARCHIVED hide rule as department
                     (Positions Rule 2), and gated behind idea-MDP-581 (header-level
                     gate: hides the control until the flag resolves true, so the tag
                     never flickers into its default-shown state). The tag and dialog
                     additionally self-gate behind the same flag. -->
                <template v-if="canAssignJobFamily && ideaMdp581Enabled">
                  <AssignedJobFamilyTag
                    :job-family="resolvedJobFamily"
                    :level="resolvedJobFamilyLevel"
                    :disabled="isAssigningJobFamily"
                    @assign="openJobFamilyDialog"
                    @unassign="handleJobFamilyUnassign"
                  />
                  <JobFamilyPickerDialog
                    :open="jobFamilyDialogOpen"
                    :results="jobFamilyResults"
                    :loading="jobFamilyLoading"
                    :busy="isAssigningJobFamily"
                    :default-expanded="true"
                    :visible-row-count="5"
                    @update:open="jobFamilyDialogOpen = $event"
                    @search="handleJobFamilySearch"
                    @assign="handleJobFamilyPickerAssign"
                    @cancel="jobFamilyDialogOpen = false"
                  />
                  <DetachPositionDialog
                    v-if="showDetachJobFamilyDialog"
                    v-model="showDetachJobFamilyDialog"
                    parent-kind="job-family"
                    action-label="Remove"
                    :position_name="positionReleaseLabel"
                    :parent_name="jobFamilyName"
                    @onAction="handleConfirmUnassignJobFamily"
                  />
                </template>
                <!-- Job Category control — gated behind account.job_categories_on
                     (Rule 4/13) AND the idea-MDP-566 kill switch. Unlike the
                     department/job-family controls, Retired/Archived positions
                     still show the tag read-only (no ×) per Rule 8, so the
                     gate is on the account toggle + flag, not on editability. -->
                <template v-if="showJobCategoryControl">
                  <AssignedJobCategoryTag
                    :filled="jobCategoryFilled"
                    :job-category-name="jobCategoryName"
                    :total-score="jobCategoryTotalScore"
                    :readonly="jobCategoryReadonly"
                    @assign="openJobCategoryDialog"
                    @unassign="handleJobCategoryUnassign"
                  />
                  <JobCategoryPickerDialog
                    :model-value="jobCategorySelection"
                    @update:modelValue="jobCategorySelection = $event"
                    :open="jobCategoryDialogOpen"
                    :results="jobCategoryResults"
                    :loading="jobCategoryLoading"
                    :busy="isAssigningJobCategory"
                    :error="jobCategoryError"
                    @update:open="jobCategoryDialogOpen = $event"
                    @search="handleJobCategorySearch"
                    @assign="handleJobCategoryPickerAssign"
                    @cancel="jobCategoryDialogOpen = false"
                  />
                  <DetachPositionDialog
                    v-if="showDetachJobCategoryDialog"
                    v-model="showDetachJobCategoryDialog"
                    parent-kind="job-category"
                    action-label="Remove"
                    :position_name="positionReleaseLabel"
                    :parent_name="jobCategoryName"
                    @onAction="handleConfirmUnassignJobCategory"
                  />
                </template>
                <ReportingToTag
                  v-if="reportingTagPositionCode || !ideaMdp450Enabled"
                  :editable="true"
                  :position_code="reportingTagPositionCode"
                  :position_name="has_breadcrumb ? null : reportingPositionName"
                  :employee_name="has_breadcrumb ? null : reportingEmployeeName"
                  :avatar="has_breadcrumb ? null : reportingAvatar"
                  @replace="handleReportingReplace"
                  @remove="handleReportingRemove"
                />
                <OccupyingPositionBadge :employee="resolvedOccupyingEmployee" />
                <PreparingForPositionBadge :preparing_for="resolvedPreparingFor" />
              </template>
            </div>
            <div class="position-page-header__name-code">
              <span class="position-page-header__name">{{ positionName }}</span>
              <span v-if="code" class="position-page-header__code">{{ code }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Icons -->
      <div class="position-page-header__actions">
        <ChatterIcon
          :unread="chatterUnreadCount"
          :is_white="false"
          @onClick="handleChatterClick"
          class="position-page-header__chatter-icon"
        />
        <MenuIcon
          :dropdownItems="menuItems"
          :showTooltip="false"
          @onClick="handleMenuClick"
          class="position-page-header__menu-icon"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from "vue";
import {
  LD_FLAG_IDEA_MDP_440,
  LD_FLAG_IDEA_MDP_450,
  LD_FLAG_IDEA_MDP_566,
  LD_FLAG_IDEA_MDP_581,
} from "@components/constants/launchDarklyFlagKeys";
import {
  getPositionId,
  getListOfDepartments,
  assignPositionToDepartment,
  releasePositionFromDepartment,
  setJobFamily,
  unassignJobFamily,
  getAccountData,
} from "@/api";
import {
  getListOfJobCategories,
  assignPositionJobCategory,
  unassignPositionJobCategory,
} from "@/api/jobCategoryAPI";
import { loadJobFamilyPickerResults } from "@components/JobFamilyPickerDialog/loadJobFamilyPickerResults";
import { useNotificationStore } from "@/store/pinia/notifications";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import ReadinessChip from "@components/ReadinessChip/ReadinessChip.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import DepartmentTag from "@components/DepartmentTag/DepartmentTag.vue";
import DepartmentTagInPositionForm from "@components/DepartmentTagInPositionForm/DepartmentTagInPositionForm.vue";
import DepartmentPickerDialog from "@components/DepartmentPickerDialog/DepartmentPickerDialog.vue";
import DetachPositionDialog from "@components/DetachPositionDialog/DetachPositionDialog.vue";
import ReportingToTag from "@components/ReportingToTag/ReportingToTag.vue";
import OccupyingPositionBadge from "@components/OccupyingPositionBadge/OccupyingPositionBadge.vue";
import PreparingForPositionBadge from "@components/PreparingForPositionBadge/PreparingForPositionBadge.vue";
import AssignedJobFamilyTag from "@components/AssignedJobFamilyTag/AssignedJobFamilyTag.vue";
import JobFamilyPickerDialog from "@components/JobFamilyPickerDialog/JobFamilyPickerDialog.vue";
import AssignedJobCategoryTag from "@components/AssignedJobCategoryTag/AssignedJobCategoryTag.vue";
import JobCategoryPickerDialog from "@components/JobCategoryPickerDialog/JobCategoryPickerDialog.vue";

const props = defineProps({
  /** Position name */
  positionName: {
    type: String,
    required: true,
  },
  /** Position status */
  status: {
    type: String,
    default: "",
  },
  /** Position code */
  code: {
    type: String,
    default: "",
  },
  /** Show/hide breadcrumb */
  has_breadcrumb: {
    type: Boolean,
    default: true,
  },
  /** Breadcrumb items. Default: Positions > Position */
  breadcrumbsList: {
    type: Array,
    default: () => [
      {
        text: "Positions",
        disabled: false,
        href: "/organization/positions",
      },
      {
        text: "Position",
        disabled: true,
      },
    ],
  },
  /** Unread chatter count */
  chatterUnreadCount: {
    type: Number,
    default: 0,
  },
  /** Menu items for MenuIcon */
  menuItems: {
    type: Array,
    default: () => [],
  },
  /** Department object for DepartmentTagInPositionForm. null → "Assign Department" button. */
  department: {
    type: Object,
    default: () => null,
  },
  /** Reporting-to position code (shown when has_breadcrumb is false).
   *  Must be non-null for the "occupied-position-editable" state. */
  reportingPositionCode: {
    type: String,
    default: "0000",
  },
  /** Reporting-to position name */
  reportingPositionName: {
    type: String,
    default: "Position Name",
  },
  /** Reporting-to employee name. Required (non-null) so the avatar renders,
   *  producing the "occupied-position-editable" state instead of vacant. */
  reportingEmployeeName: {
    type: String,
    default: "Employee Name",
  },
  /** Reporting-to avatar URL */
  reportingAvatar: {
    type: String,
    default: null,
  },
  /** `{ percent, changePercent }` for the inline ReadinessChip after the status badge
   *  (behind idea-MDP-450). `null` = no readiness data, no chip. */
  readiness: {
    type: Object,
    default: null,
  },
  /** Employee object for OccupyingPositionBadge. Must have a `name` so the
   *  badge renders the "occupied" state (avatar) instead of "Not Assigned". */
  occupyingEmployee: {
    type: [Object, Array],
    default: () => ({ name: "Employee Name", picture: null }),
  },
  /** People preparing for this position (PreparingForPositionBadge).
   *  `null` or empty array renders the "not-assigned" state. */
  preparingFor: {
    type: Array,
    default: () => null,
  },
  /** Account ID used to fetch live position data from the API.
   *  Required for the API call to be made. */
  accountId: {
    required: false,
    default: null,
    validator: (v) =>
      v === null || v === undefined || typeof v === "number" || typeof v === "string",
  },
  /** Position ID used to fetch live position data from the API.
   *  Required for the API call to be made. */
  positionId: {
    required: false,
    default: null,
    validator: (v) =>
      v === null || v === undefined || typeof v === "number" || typeof v === "string",
  },
  /** When non-empty, allows the header to load occupying/preparing data via
   *  the shared axios API (`getPositionId`). Host comes from app axios config
   *  (see main.js), not from this string — the prop is a gate so Storybook /
   *  isolated use can omit network calls when set to ''. */
  apiBaseUrl: {
    type: String,
    default: "",
  },
  /** When false, hides the Department / ReportingTo / Occupying / Preparing
   *  badges and skips the related API fetch. Used by `PositionForm` together
   *  with the LD `idea-MDP-443` flag to render the legacy simple header
   *  (breadcrumb + status + name + code) when the flag is off. Defaults to
   *  true to preserve behavior in other consumers (e.g. `ExOrgPosition`). */
  showExpandedDetails: {
    type: Boolean,
    default: true,
  },
});

const { proxy } = getCurrentInstance() || {};
const ideaMdp440Enabled = ref(false);
const ideaMdp450Enabled = ref(false);
const ideaMdp566Enabled = ref(false);
const ideaMdp581Enabled = ref(false);

const emit = defineEmits([
  "breadcrumb-click",
  "chatter-click",
  "menu-click",
  "department-assign",
  "department-remove",
  "reporting-replace",
  "reporting-remove",
  "job-family-assign",
  "job-family-unassign",
  "job-category-assign",
  "job-category-unassign",
]);

const handleBreadcrumbClick = (breadcrumb) => {
  emit("breadcrumb-click", breadcrumb);
};

const handleChatterClick = () => {
  emit("chatter-click");
};

const handleMenuClick = (item) => {
  emit("menu-click", item);
};

const departmentDialogOpen = ref(false);
const showDetachDepartmentDialog = ref(false);
const departmentTree = ref(null);
const isAssigningDepartment = ref(false);
const isReleasingDepartment = ref(false);

const openDepartmentDialog = async () => {
  departmentDialogOpen.value = true;
  if (departmentTree.value !== null) return;
  try {
    const accountId = props.accountId ?? localStorage.getItem("currentAccountID");
    const { data } = await getListOfDepartments(accountId, "", { status: ["active", "draft"] });
    departmentTree.value = Array.isArray(data) ? data : [];
  } catch {
    departmentTree.value = [];
  }
};

const mapApiDepartmentToTagFormat = (department) => {
  if (!department?.id) return null;

  const path = [];
  let current = department;
  let leafId = department.id;

  while (current) {
    if (current.name) path.push(current.name);
    leafId = current.id;
    current = current.sub_department ?? null;
  }

  return { id: leafId, path };
};

const handleDepartmentPickerAssign = async ({ department }) => {
  const accountId = props.accountId ?? localStorage.getItem("currentAccountID");
  if (!accountId || !props.positionId || !department?.id) return;

  isAssigningDepartment.value = true;
  try {
    await assignPositionToDepartment(accountId, department.id, props.positionId);
    await fetchPositionData();
    departmentDialogOpen.value = false;
    useNotificationStore().success("Position assigned to department");
    emit("department-assign", { department: fetchedDepartment.value });
  } catch (error) {
    console.error("Error assigning department:", error);
    useNotificationStore().error("Failed to assign position to department");
  } finally {
    isAssigningDepartment.value = false;
  }
};

const handleDepartmentAssign = () => {
  emit("department-assign");
};

const handleDepartmentRemove = () => {
  if (!resolvedDepartment.value?.id) return;
  if (ideaMdp440Enabled.value) {
    showDetachDepartmentDialog.value = true;
    return;
  }
  emit("department-remove");
};

const handleConfirmReleaseDepartment = async () => {
  const accountId = props.accountId ?? localStorage.getItem("currentAccountID");
  const departmentId = resolvedDepartment.value?.id;
  if (!accountId || !props.positionId || !departmentId || isReleasingDepartment.value) return;

  isReleasingDepartment.value = true;
  // Drive the popup action-button loader (ExActionsPopUp reads $loader.isAppLoading)
  // and keep the dialog open until the request settles instead of closing eagerly.
  proxy?.$loader?.setAppLoading(true);
  try {
    await releasePositionFromDepartment(accountId, departmentId, {
      position_id: props.positionId,
    });
    await fetchPositionData();
    showDetachDepartmentDialog.value = false;
    useNotificationStore().success("Position released from department");
    emit("department-remove");
  } catch (error) {
    console.error("Error releasing department:", error);
    useNotificationStore().error("Failed to release position from department");
  } finally {
    proxy?.$loader?.setAppLoading(false);
    isReleasingDepartment.value = false;
  }
};

// --- Job Family assignment (MDP-581 §2 / §14) -----------------------------
const jobFamilyDialogOpen = ref(false);
const jobFamilyResults = ref([]);
const jobFamilyLoading = ref(false);
const isAssigningJobFamily = ref(false);
const showDetachJobFamilyDialog = ref(false);

const openJobFamilyDialog = () => {
  jobFamilyResults.value = [];
  jobFamilyDialogOpen.value = true;
};

const loadJobFamilyResults = async (query = "") => {
  const accountId = props.accountId ?? localStorage.getItem("currentAccountID");
  if (!accountId) {
    jobFamilyResults.value = [];
    jobFamilyLoading.value = false;
    return;
  }

  jobFamilyLoading.value = true;
  try {
    jobFamilyResults.value = await loadJobFamilyPickerResults(accountId, query);
  } catch {
    jobFamilyResults.value = [];
  } finally {
    jobFamilyLoading.value = false;
  }
};

const handleJobFamilySearch = (query) => {
  loadJobFamilyResults(query);
};

const handleJobFamilyPickerAssign = async ({ jobFamilyId, levelId }) => {
  const accountId = props.accountId ?? localStorage.getItem("currentAccountID");
  if (
    !accountId ||
    !props.positionId ||
    !jobFamilyId ||
    !levelId ||
    isAssigningJobFamily.value
  ) {
    return;
  }

  isAssigningJobFamily.value = true;
  try {
    await setJobFamily(accountId, props.positionId, {
      job_family_id: jobFamilyId,
      job_family_level_id: levelId,
    });
    await fetchPositionData();
    jobFamilyDialogOpen.value = false;
    useNotificationStore().success("Position assigned to job family");
    emit("job-family-assign", {
      jobFamily: fetchedJobFamily.value,
      level: fetchedJobFamilyLevel.value,
    });
  } catch (error) {
    console.error("Error assigning job family:", error);
    useNotificationStore().error("Failed to assign job family");
  } finally {
    isAssigningJobFamily.value = false;
  }
};

const handleJobFamilyUnassign = () => {
  if (!resolvedJobFamily.value) return;
  if (isAssigningJobFamily.value) return;
  showDetachJobFamilyDialog.value = true;
};

const handleConfirmUnassignJobFamily = async () => {
  const accountId = props.accountId ?? localStorage.getItem("currentAccountID");
  if (!accountId || !props.positionId || isAssigningJobFamily.value) return;

  isAssigningJobFamily.value = true;
  // Drive the popup action-button loader (ExActionsPopUp reads $loader.isAppLoading)
  // and keep the dialog open until the request settles instead of closing eagerly.
  proxy?.$loader?.setAppLoading(true);
  try {
    await unassignJobFamily(accountId, props.positionId);
    await fetchPositionData();
    showDetachJobFamilyDialog.value = false;
    useNotificationStore().success("Position unassigned from job family");
    emit("job-family-unassign");
  } catch (error) {
    console.error("Error unassigning job family:", error);
    useNotificationStore().error("Failed to unassign job family");
  } finally {
    proxy?.$loader?.setAppLoading(false);
    isAssigningJobFamily.value = false;
  }
};

// --- Job Category assignment (MDP-566 · Rules 6/12/13 · ER-12045) --------
// The tag renders only when the account's Jobs toggle is on AND the
// idea-MDP-566 kill switch is on (Rule 4/13). The category itself is read
// from the position detail payload (see fetchPositionData), matching how Job
// Family is loaded, instead of a second `current` endpoint round-trip.
const accountJobCategoriesOn = ref(false);
const jobCategoryDialogOpen = ref(false);
const jobCategorySelection = ref(null);
const jobCategoryResults = ref([]);
const jobCategoryLoading = ref(false);
const isAssigningJobCategory = ref(false);
const showDetachJobCategoryDialog = ref(false);
const jobCategoryError = ref("");

const showJobCategoryControl = computed(
  () => accountJobCategoriesOn.value && ideaMdp566Enabled.value
);

const fetchJobCategoriesSetting = async () => {
  if (!props.showExpandedDetails) return;
  const accountId = props.accountId ?? localStorage.getItem("currentAccountID");
  if (!accountId || !props.apiBaseUrl) return;
  try {
    const { data } = await getAccountData(accountId);
    accountJobCategoriesOn.value = data?.job_categories_on === true;
  } catch {
    // Same as JobsConfiguration: a failed read defaults the toggle off.
    accountJobCategoriesOn.value = false;
  }
};

const openJobCategoryDialog = () => {
  jobCategoryError.value = "";
  jobCategorySelection.value = null;
  jobCategoryResults.value = [];
  jobCategoryDialogOpen.value = true;
};

const mapJobCategoryPickerResult = (category) => ({
  jobCategoryId: category?.id,
  name: String(category?.name || "").trim(),
  code: category?.code_str || category?.code || "",
  status: category?.status || "",
  totalScore: category?.total_score ?? null,
});

const handleJobCategorySearch = async (query) => {
  const accountId = props.accountId ?? localStorage.getItem("currentAccountID");
  if (!accountId) {
    jobCategoryResults.value = [];
    jobCategoryLoading.value = false;
    return;
  }

  jobCategoryLoading.value = true;
  try {
    const { data } = await getListOfJobCategories(accountId, {
      search: typeof query === "string" ? query : "",
      status: ["draft", "active", "retired"],
    });
    const rows = Array.isArray(data?.results) ? data.results : [];
    jobCategoryResults.value = rows.map(mapJobCategoryPickerResult);
  } catch {
    jobCategoryResults.value = [];
  } finally {
    jobCategoryLoading.value = false;
  }
};

function jobCategoryErrorMessage(error) {
  const code = error?.response?.data?.error;
  if (code === "JOB_CATEGORY_NOT_ACTIVE") {
    return "Only Active job categories can be assigned.";
  }
  const detail = error?.response?.data?.details || error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  return "Failed to assign job category.";
}

const handleJobCategoryPickerAssign = async ({ jobCategoryId }) => {
  const accountId = props.accountId ?? localStorage.getItem("currentAccountID");
  if (
    !accountId ||
    !props.positionId ||
    !jobCategoryId ||
    isAssigningJobCategory.value
  ) {
    return;
  }

  isAssigningJobCategory.value = true;
  jobCategoryError.value = "";
  try {
    await assignPositionJobCategory(accountId, props.positionId, {
      assignment_type: "SUBSCRIBED",
      job_category_id: jobCategoryId,
    });
    await fetchPositionData();
    jobCategoryDialogOpen.value = false;
    useNotificationStore().success("Position assigned to job category");
    emit("job-category-assign", { jobCategory: fetchedJobCategory.value });
  } catch (error) {
    // Rule 12: server errors (e.g. JOB_CATEGORY_NOT_ACTIVE) show inline in the
    // dialog and the dialog stays open so the user can pick another category.
    console.error("Error assigning job category:", error);
    jobCategoryError.value = jobCategoryErrorMessage(error);
  } finally {
    isAssigningJobCategory.value = false;
  }
};

const handleJobCategoryUnassign = () => {
  if (!resolvedJobCategory.value) return;
  if (isAssigningJobCategory.value) return;
  showDetachJobCategoryDialog.value = true;
};

const handleConfirmUnassignJobCategory = async () => {
  const accountId = props.accountId ?? localStorage.getItem("currentAccountID");
  if (!accountId || !props.positionId || isAssigningJobCategory.value) return;

  isAssigningJobCategory.value = true;
  proxy?.$loader?.setAppLoading(true);
  try {
    await unassignPositionJobCategory(accountId, props.positionId);
    await fetchPositionData();
    showDetachJobCategoryDialog.value = false;
    useNotificationStore().success("Position unassigned from job category");
    emit("job-category-unassign");
  } catch (error) {
    console.error("Error unassigning job category:", error);
    useNotificationStore().error("Failed to unassign job category");
  } finally {
    proxy?.$loader?.setAppLoading(false);
    isAssigningJobCategory.value = false;
  }
};

const handleReportingReplace = () => {
  emit("reporting-replace");
};

const handleReportingRemove = () => {
  emit("reporting-remove");
};

// --- API-driven data for Department / Occupying / Preparing badges -------
const fetchedDepartment = ref(undefined);
const fetchedOccupiedBy = ref(undefined);
const fetchedPreparingFor = ref(undefined);
const fetchedJobFamily = ref(undefined);
const fetchedJobFamilyLevel = ref(undefined);
const fetchedJobCategory = ref(undefined);

const fetchPositionData = async () => {
  if (!props.showExpandedDetails) return;
  if (!props.accountId || !props.positionId || !props.apiBaseUrl) return;
  try {
    const { data } = await getPositionId(props.accountId, props.positionId);

    fetchedDepartment.value = data?.department
      ? mapApiDepartmentToTagFormat(data.department)
      : null;

    // Position detail may expose the assigned employee under several field
    // names depending on the API version / position type:
    //   - `primary_occupied_by`  (array)  — primary positions, newer API
    //   - `occupied_by`          (object) — non-primary / legacy API
    //   - `assigned_primary`     (object) — used by the positions list API;
    //                                        detail endpoint sometimes mirrors it
    const primary = Array.isArray(data?.primary_occupied_by) ? data.primary_occupied_by : null;
    const primaryEmployee = primary && primary.length > 0 ? primary[0] : null;
    // `??` keeps a non-null primaryEmployee; falls through to the first
    // truthy fallback field for every other position shape.
    fetchedOccupiedBy.value =
      primaryEmployee ?? data?.occupied_by ?? data?.assigned_primary ?? null;

    // `preparing_for` or `assigned_preparing_for` depending on API version.
    fetchedPreparingFor.value = Array.isArray(data?.preparing_for)
      ? data.preparing_for
      : Array.isArray(data?.assigned_preparing_for)
        ? data.assigned_preparing_for
        : [];

    // Position → Job Family/Level link (delivered by ER-11114). `null` = unassigned.
    fetchedJobFamily.value = data?.job_family ?? null;
    fetchedJobFamilyLevel.value = data?.job_family_level ?? null;

    // Position → Job Category subscription (delivered by ER-12042, Rule 9).
    // `null` = unassigned. `total_score` is already the summed four-factor
    // score (null when any factor is unscored), so no separate `current` call
    // is needed — this detail endpoint carries everything the tag renders.
    fetchedJobCategory.value = data?.job_category ?? null;
  } catch {
    // Auth / network errors: axios interceptor handles 401; avoid credentialed
    // cross-origin fetch (CORS) — same pattern as the rest of the app (Token header).
  }
};

onMounted(async () => {
  try {
    ideaMdp440Enabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_440, false)) === true;
  } catch {
    ideaMdp440Enabled.value = false;
  }
  try {
    ideaMdp450Enabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_450, false)) === true;
  } catch {
    ideaMdp450Enabled.value = false;
  }
  try {
    ideaMdp581Enabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, false)) === true;
  } catch {
    ideaMdp581Enabled.value = false;
  }
  try {
    ideaMdp566Enabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_566, false)) === true;
  } catch {
    ideaMdp566Enabled.value = false;
  }
  await Promise.all([fetchPositionData(), fetchJobCategoriesSetting()]);
});
// Also watch `showExpandedDetails`: if the LD flag resolves to `true` after
// the component mounts (common on page-load), the fetch would otherwise never
// run because accountId/positionId/apiBaseUrl haven't changed.
// Normalise positionId to string so a type change from route-param "123"
// → API-returned 123 (number) does not trigger a redundant double-fetch.
watch(
  () => [
    props.accountId,
    String(props.positionId ?? ""),
    props.apiBaseUrl,
    props.showExpandedDetails,
  ],
  fetchPositionData
);
// Account Jobs toggle (job_categories_on) is independent of the position id;
// re-read it when the account or the expanded-details gate changes.
watch(
  () => [props.accountId, props.apiBaseUrl, props.showExpandedDetails],
  fetchJobCategoriesSetting
);

// Use fetched API values when available, otherwise fall back to props.
const resolvedDepartment = computed(() => {
  if (fetchedDepartment.value !== undefined) return fetchedDepartment.value;
  if (!props.department?.id) return props.department;
  if (Array.isArray(props.department.path)) return props.department;
  return mapApiDepartmentToTagFormat(props.department);
});

// RETIRED/ARCHIVED positions are always orphans (Positions Rule 2): the
// department and job-family controls are hidden entirely (hide-don't-disable).
const isRetiredOrArchived = (status) => {
  const s = String(status || "").toLowerCase();
  return s === "retired" || s === "archived";
};

const canAssignDepartment = computed(() => !isRetiredOrArchived(props.status));

const canAssignJobFamily = computed(() => !isRetiredOrArchived(props.status));

const positionReleaseLabel = computed(() => {
  const name = String(props.positionName || "").trim();
  const code = String(props.code || "").trim();
  if (code && name) return `${code} - ${name}`;
  if (name) return name;
  return code;
});

const departmentLeafName = computed(() => {
  const path = resolvedDepartment.value?.path;
  if (!Array.isArray(path) || path.length === 0) return "";
  return path[path.length - 1];
});

const departmentLabel = computed(() => departmentLeafName.value);

const resolvedOccupyingEmployee = computed(() =>
  fetchedOccupiedBy.value !== undefined ? fetchedOccupiedBy.value : props.occupyingEmployee
);

const resolvedPreparingFor = computed(() =>
  fetchedPreparingFor.value !== undefined ? fetchedPreparingFor.value : props.preparingFor
);

// The AssignedJobFamilyTag expects `{ id, name } | null` for the family and
// `{ id, ordinal, name } | null` for the level — exactly what the position
// detail endpoint returns (`job_family` / `job_family_level`). Null → unassigned CTA.
const resolvedJobFamily = computed(() =>
  fetchedJobFamily.value !== undefined ? fetchedJobFamily.value : null
);

const resolvedJobFamilyLevel = computed(() =>
  fetchedJobFamilyLevel.value !== undefined ? fetchedJobFamilyLevel.value : null
);

const jobFamilyName = computed(() => resolvedJobFamily.value?.name || "");

// AssignedJobCategoryTag expects the present Job Category subscription or
// null (unassigned). The detail endpoint returns { id, name, status, code_str,
// total_score, assignment_type } per Rule 9; null → the "+ Job Category" CTA.
const resolvedJobCategory = computed(() =>
  fetchedJobCategory.value !== undefined ? fetchedJobCategory.value : null
);

const jobCategoryFilled = computed(() => !!resolvedJobCategory.value);

const jobCategoryName = computed(() => resolvedJobCategory.value?.name || "");

const jobCategoryTotalScore = computed(() => {
  const score = resolvedJobCategory.value?.total_score;
  return score === null || score === undefined ? null : Number(score);
});

// Retired/Archived positions are read-only (Rule 8): the tag keeps its name
// but drops the × (and an empty position renders nothing).
const jobCategoryReadonly = computed(() => isRetiredOrArchived(props.status));

// Reporting-to position code shown by ReportingToTag. `null` is the
// "Top of OrgChart" state, which is hidden when LD idea-MDP-450 is on.
const reportingTagPositionCode = computed(() =>
  props.has_breadcrumb ? null : props.reportingPositionCode
);
</script>

<style scoped>
.position-page-header {
  font-family: Inter, sans-serif;
  color: #344054;
}

/* Breadcrumb Section */
.position-page-header__breadcrumbs {
  margin-bottom: 12px;
}

/* Main Header Section */
.position-page-header__main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
}

.position-page-header__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.position-page-header__icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.position-page-header__icon-svg {
  width: 60px;
  height: 60px;
}

.position-page-header__info {
  flex: 1;
  min-width: 0;
}

.position-page-header__status {
  flex-shrink: 0;
}

.position-page-header__name-code {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.position-page-header__name {
  color: var(--Big-Headings, #101828);
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0em;
  text-align: left;
}

.position-page-header__code {
  color: var(--Place-Holder-Text, #98a2b3);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0em;
  font-family: Inter;
  text-align: left;
}

/* Action Icons */
.position-page-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.position-page-header__chatter-icon {
  cursor: pointer;
}

.position-page-header__menu-icon {
  cursor: pointer;
}

.position-page-header__meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.position-page-header__status-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.position-page-header__breadcrumbs .v-breadcrumbs {
  gap: 4px;
  padding: 0;
}

.position-page-header__breadcrumbs .v-breadcrumbs__item {
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.2px;
  color: #475467 !important;
  background: transparent !important;
  border: none !important;
  height: auto;
  padding: 0;
  display: inline-flex;
  align-items: center;
}

.position-page-header__breadcrumbs .v-breadcrumbs__item:first-child {
  padding: 0;
  color: #475467 !important;
}

.position-page-header__breadcrumbs .v-breadcrumbs__item--disabled {
  height: 28px;
  padding: 6px 14px;
  border-radius: 999px;
  background: #f4edff !important;
  border: 1px solid #e4d5ff;
  color: #7f56d9 !important;
  font-weight: 600;
}

.position-page-header__breadcrumbs .v-breadcrumbs__divider svg {
  width: 16px;
  height: 16px;
  stroke: #d6bbfb;
}
</style>
